import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import {
  PatientSearchContainer,
  PatientSearchInput,
  PatientSearchResults,
  PatientSearchResultItem,
  PatientName,
  PatientInfo,
  NoResults,
  LoadingSpinner,
} from '../../Css/StyledCalendarModal.js';

const PatientSearch = ({ 
  value, 
  onChange, 
  onSelect, 
  placeholder = "Buscar paciente..." 
}) => {
  // Traemos la lista de pacientes desde Redux.
  // Si todavía no cargó, usamos array vacío para evitar errores.
  const pacientes = useSelector((state) => state.informacion?.lista_pacientes || []);

  const [searchTerm, setSearchTerm] = useState(value || '');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const searchTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Mantiene sincronizado el input si cambia el value desde el componente padre.
  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  // Buscar pacientes directamente desde Redux.
  // Ya no usamos backend.
  const performPatientSearch = (term) => {
    setIsLoading(true);

    const searchTerm = term.trim().toLowerCase();

    const filteredPatients = pacientes.filter((patient) => {
      const nombre = patient.nombre?.toLowerCase() || '';
      const apellido = patient.apellido?.toLowerCase() || '';
      const email = patient.email?.toLowerCase() || '';
      const email1 = patient.email_1?.toLowerCase() || '';
      const email2 = patient.email_2?.toLowerCase() || '';

      const fullName = `${nombre} ${apellido}`;

      return (
        fullName.includes(searchTerm) ||
        nombre.includes(searchTerm) ||
        apellido.includes(searchTerm) ||
        email.includes(searchTerm) ||
        email1.includes(searchTerm) ||
        email2.includes(searchTerm)
      );
    });

    setResults(filteredPatients);
    setIsLoading(false);
  };

  // Manejar cambios en el input.
  const handleInputChange = (e) => {
    const term = e.target.value;

    setSearchTerm(term);
    onChange(term);

    // Limpiar timeout anterior.
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Buscamos recién desde 2 letras para no mostrar toda la lista.
    if (term.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performPatientSearch(term);
        setShowResults(true);
      }, 300);
    } else {
      setResults([]);
      setShowResults(false);
    }
  };

  // Manejar selección de paciente.
  const handlePatientSelect = (patient) => {
    const fullName = `${patient.nombre || ''} ${patient.apellido || ''}`.trim();

    setSearchTerm(fullName);
    setShowResults(false);
    setResults([]);

    // Mandamos el paciente completo al CalendarModal.
    onSelect(patient);
  };

  // Cerrar resultados al hacer click fuera.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Limpiar timeout al desmontar.
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <PatientSearchContainer ref={containerRef}>
      <PatientSearchInput
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        placeholder={placeholder}
        autoComplete="off"
      />
      
      {showResults && (
        <PatientSearchResults>
          {isLoading ? (
            <LoadingSpinner>Cargando...</LoadingSpinner>
          ) : results.length > 0 ? (
            results.map((patient) => (
              <PatientSearchResultItem
                key={patient.id_paciente}
                onMouseDown={() => handlePatientSelect(patient)}
              >
                <PatientName>
                  {patient.nombre} {patient.apellido}
                </PatientName>

                <PatientInfo>
                  {patient.email || patient.email_1 || patient.email_2 || 'Sin email cargado'}
                </PatientInfo>
              </PatientSearchResultItem>
            ))
          ) : (
            <NoResults>No se encontraron pacientes</NoResults>
          )}
        </PatientSearchResults>
      )}
    </PatientSearchContainer>
  );
};

export default PatientSearch;